import React, { useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import "./groupCodeStandardSearch.css";
import domain_icon from "assets/domain_icon.svg";
import { SIDEBAR_BUTTONS, useSidebarTracking } from "hooks/useSidebarTracking";

export const StandardNavigationTree = ({
  standards,
  selectedStandard,
  expandedNodes,
  onToggleNode,
  onSelectStandard,
  // State-framework trees carry standards mapped to zero questions that the
  // teacher must still be able to pick (with the detail-view warning). The
  // default keeps the Common Core rule: zero-count nodes are hidden.
  showZeroCountStandards = false,
}) => {
  const isNodeExpanded = (nodeId) => expandedNodes[nodeId] || false;

  // Tracked here, on the teacher's own clicks, so a selection made for them
  // (a search result, a URL restore) never reads as a sidebar click.
  const trackSidebar = useSidebarTracking();
  const toggleNode = (nodeLevel, code) => {
    trackSidebar(SIDEBAR_BUTTONS.TREE_TOGGLE, {
      node_level: nodeLevel,
      node_code: code,
      action: isNodeExpanded(code) ? "Collapse" : "Expand",
    });
    onToggleNode(code);
  };
  const trackSelectNode = (nodeLevel, code) =>
    trackSidebar(SIDEBAR_BUTTONS.SELECT_STANDARD, { node_level: nodeLevel, standard_code: code });

  const countHidesNode = (node) =>
    !showZeroCountStandards && (node.count === 0 || node.challenge_count === 0);

  const visibleStandardsOf = (cluster) =>
    showZeroCountStandards
      ? cluster.standards || []
      : (cluster.standards || []).filter((std) => std.count !== 0 && std.challenge_count !== 0);

  // A search selection can land below the sidebar fold (the highlight then
  // reads as "nothing happened"); attach to the selected node so it scrolls
  // into view whenever the selection moves. No-op when already visible.
  const selectedNodeRef = useCallback((node) => {
    if (node) node.scrollIntoView({ block: "nearest" });
  }, []);

  const isStandardSelected = (standardCode) => {
    if (!selectedStandard) return false;
    // Only highlight if a standard (not grade/domain/cluster) is selected
    // Standards don't have a 'type' property, they have a 'code' property
    return (
      selectedStandard.type === undefined &&
      selectedStandard.code !== undefined &&
      selectedStandard.code === standardCode
    );
  };

  const isNodeSelected = (nodeId, nodeType) => {
    if (!selectedStandard) return false;
    if (!selectedStandard.type) return false; // Not a parent node

    // Only highlight if this is the exact selected node, not its parents
    switch (nodeType) {
      case 'grade':
        return selectedStandard.type === 'grade' && selectedStandard.grade === nodeId;
      case 'domain':
        return selectedStandard.type === 'domain' && selectedStandard.code === nodeId;
      case 'cluster':
        return selectedStandard.type === 'cluster' && selectedStandard.code === nodeId;
      default:
        return false;
    }
  };

  const renderStandard = (standard, grade, domain, cluster) => {
    // Hide standards with challenge_count of 0 (state trees opt out)
    if (countHidesNode(standard)) {
      return null;
    }

    const isSelected = isStandardSelected(standard.code);

    return (
      <div
        key={standard.code}
        ref={isSelected ? selectedNodeRef : null}
        className={`tree-item tree-standard ${isSelected ? "selected" : ""}`}
      >
        <div className="tree-item-content">
          <button
            type="button"
            className="tree-row-btn"
            aria-label={`Standard ${standard.code}: ${standard.name}`}
            onClick={(e) => {
              e.stopPropagation();
              trackSelectNode("Standard", standard.code);
              onSelectStandard(standard, grade, domain, cluster);
            }}
          >
            <span className="tree-label">
              <span className="tree-code">STANDARD - {standard.code}</span>
              <span className="tree-name">{standard.name}</span>
            </span>
          </button>
        </div>
      </div>
    );
  };

  const renderCluster = (cluster, grade, domain) => {
    // Hide clusters with challenge_count of 0 (state trees opt out)
    if (countHidesNode(cluster)) {
      return null;
    }

    // Filter out standards with challenge_count of 0
    const visibleStandards = visibleStandardsOf(cluster);

    // Hide cluster if it has no visible standards
    if (visibleStandards.length === 0) {
      return null;
    }

    const isExpanded = isNodeExpanded(cluster.code);
    const isSelected = isNodeSelected(cluster.code, 'cluster');
    const hasStandards = visibleStandards.length > 0;

    return (
      <div key={cluster.code} ref={isSelected ? selectedNodeRef : null} className={`tree-item tree-cluster ${isSelected ? "selected" : ""}`}>
        <div className="tree-item-content">
          {hasStandards && (
            <button
              type="button"
              className="tree-chevron-btn"
              aria-label={isExpanded ? "Collapse" : "Expand"}
              aria-expanded={isExpanded}
              onClick={(e) => {
                e.stopPropagation();
                toggleNode("Cluster", cluster.code);
              }}
            >
              <FontAwesomeIcon
                icon={isExpanded ? faChevronDown : faChevronRight}
                className="tree-chevron"
              />
            </button>
          )}
          <button
            type="button"
            className="tree-row-btn"
            aria-label={`Cluster ${cluster.code}: ${cluster.name}`}
            onClick={(e) => {
              e.stopPropagation();
              trackSelectNode("Cluster", cluster.code);
              onSelectStandard({
                type: 'cluster',
                code: cluster.code,
                name: cluster.name,
                standards: visibleStandards
              }, grade, domain, cluster);
            }}
          >
            <span className="tree-label">
              <span className="tree-code">CLUSTER - {cluster.code}</span>
              <span className="tree-name">{cluster.name}</span>
            </span>
          </button>
        </div>

        {isExpanded && hasStandards && (
          <div className="tree-children">
            {visibleStandards.map((standard) =>
              renderStandard(standard, grade, domain, cluster)
            )}
          </div>
        )}
      </div>
    );
  };

  const renderDomain = (domain, grade) => {
    // Hide domains with challenge_count of 0 (state trees opt out)
    if (countHidesNode(domain)) {
      return null;
    }

    // Filter clusters to only include those with visible standards
    const visibleClusters = (domain.clusters || []).filter(cluster => {
      // Check if cluster itself has challenge_count of 0
      if (countHidesNode(cluster)) {
        return false;
      }
      // Check if cluster has any visible standards
      return visibleStandardsOf(cluster).length > 0;
    });

    // Hide domain if it has no visible clusters
    if (visibleClusters.length === 0) {
      return null;
    }

    const isExpanded = isNodeExpanded(domain.code);
    const isSelected = isNodeSelected(domain.code, 'domain');
    const hasClusters = visibleClusters.length > 0;

    return (
      <div key={domain.code} ref={isSelected ? selectedNodeRef : null} className={`tree-item tree-domain ${isSelected ? "selected" : ""}`}>
        <div className="tree-item-content">
          {hasClusters && (
            <button
              type="button"
              className="tree-chevron-btn"
              aria-label={isExpanded ? "Collapse" : "Expand"}
              aria-expanded={isExpanded}
              onClick={(e) => {
                e.stopPropagation();
                toggleNode("Domain", domain.code);
              }}
            >
              <FontAwesomeIcon
                icon={isExpanded ? faChevronDown : faChevronRight}
                className="tree-chevron"
              />
            </button>
          )}
          <button
            type="button"
            className="tree-row-btn"
            aria-label={`Domain ${domain.code}: ${domain.name}`}
            onClick={(e) => {
              e.stopPropagation();
              trackSelectNode("Domain", domain.code);
              onSelectStandard({
                type: 'domain',
                code: domain.code,
                name: domain.name,
                clusters: visibleClusters
              }, grade, domain, null);
            }}
          >
            <img src={domain_icon} className="tree-icon" alt="" />
            <span className="tree-label">
              <span className="tree-code">DOMAIN - {domain.code}</span>
              <span className="tree-name">{domain.name}</span>
            </span>
          </button>
        </div>

        {isExpanded && hasClusters && (
          <div className="tree-children">
            {visibleClusters.map((cluster) =>
              renderCluster(cluster, grade, domain)
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="navigation-tree">
      {standards.map((gradeLevel) => {
        // Filter domains to only include those with visible clusters
        const visibleDomains = (gradeLevel.domains || []).filter(domain => {
          // Check if domain itself has challenge_count of 0
          if (countHidesNode(domain)) {
            return false;
          }
          // Check if domain has any visible clusters
          const visibleClusters = (domain.clusters || []).filter(cluster => {
            if (countHidesNode(cluster)) {
              return false;
            }
            return visibleStandardsOf(cluster).length > 0;
          });
          return visibleClusters.length > 0;
        });

        const hasDomains = visibleDomains.length > 0;
        return hasDomains ? (
          <div key={gradeLevel.grade}>
            {visibleDomains.map((domain) =>
              renderDomain(domain, gradeLevel.grade)
            )}
          </div>
        ) : null;
      })}
    </div>
  );
};

